import React, { useCallback, useMemo, useState } from "react"
import {
  Typography,
  styled,
  Stack,
  Card,
  Box,
  Divider,
  useTheme,
} from "@mui/material"
import { useTranslation } from "providers/i18n"
import { Carousel, CarouselItem } from "components/Carousel"
import { PromotionCard } from "../promotions"
import { ListPrivilegeItemCard } from "../privilege"
import { WebsiteIcon } from "components/icons"
import EmailIcon from "@mui/icons-material/Email"
import LocalPhoneIcon from "@mui/icons-material/LocalPhone"
import { Link } from "react-router-dom"
import { ImageModal } from "../../Image"
import { HorizontalSlide } from "../events"
import _ from "lodash"

const Image = styled("img")(() => ({}))

export const FeatureDetail = ({ feature, loading, ...props }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const styleOverrides =
    theme.components[`AppFeatureDetail`]?.styleOverrides || {}
  const {
    root,
    section,
    galleryImage,
    divider,
    contactIcon,
    sectionTitle,
    descriptionText,
    promotionCarouselItem,
  } = styleOverrides
  const [openModal, setOpenModal] = useState(false)
  const [activeImage, setActiveImage] = useState()

  const {
    website,
    phone,
    email,
    promotions,
    description,
    gallery,
    privileges,
  } = props

  const contacts = useMemo(() => {
    return [
      { value: website, Icon: WebsiteIcon },
      { value: phone, Icon: LocalPhoneIcon },
      { value: email, Icon: EmailIcon },
    ].filter(({ value }) => value)
  }, [website, phone, email])

  const featurePromotions = useMemo(
    () =>
      promotions?.filter(({ properties }) =>
        _.includes(["store", "shop"], properties.category)
      ) || [],
    [promotions]
  )

  const handleClose = useCallback(() => {
    setOpenModal(false)
  }, [])

  const handleImage = useCallback((value) => {
    setActiveImage(value)
    setOpenModal(true)
  }, [])

  if (loading) return null

  return (
    <>
      <Card sx={root}>
        {gallery && (
          <Box sx={section}>
            <Carousel
              options={{
                fixedWidth: "182px",
                fixedHeight: "130px",
                perPage: 2,
                gap: 12,
                padding: { left: 20, right: 20 },
              }}
            >
              {gallery?.map((image, idx) => (
                <CarouselItem key={`${image.name}-${idx}`}>
                  <Image
                    onClick={() => handleImage(image.url)}
                    src={image.url}
                    alt={`${image.name}-${idx}`}
                    sx={galleryImage}
                  />
                </CarouselItem>
              ))}
            </Carousel>
          </Box>
        )}
        {description && (
          <>
            <Box sx={section}>
              <Typography component="h2" sx={sectionTitle}>
                {t("Description")}
              </Typography>
              <Typography variant="body2" sx={descriptionText}>
                {t(description)}
              </Typography>
            </Box>
            <Divider sx={divider} />
          </>
        )}
        {contacts.length > 0 && (
          <>
            <Box sx={section}>
              <Stack direction={"column"} spacing={1.5}>
                {contacts.map(({ value, Icon }) => (
                  <Stack direction={"row"} spacing={2}>
                    <Icon sx={contactIcon} />
                    <Typography variant="subtitle2">{value}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Divider sx={divider} />
          </>
        )}
        {featurePromotions?.length > 0 && (
          <>
            <Box sx={section}>
              <Typography component="h2" sx={sectionTitle}>
                {t("Promotions")}
              </Typography>
              <Stack spacing={2}>
                <HorizontalSlide>
                  {featurePromotions?.length > 0 &&
                    featurePromotions.map((featurePromotion) => (
                      <CarouselItem
                        key={featurePromotion.id}
                        className={featurePromotion.properties?.category}
                        sx={promotionCarouselItem}
                      >
                        <Link
                          to={`/promotions/${featurePromotion.id}?redirectTo=-1`}
                          key={featurePromotion.id}
                        >
                          <PromotionCard
                            promotion={featurePromotion}
                            dense={true}
                          />
                        </Link>
                      </CarouselItem>
                    ))}
                </HorizontalSlide>
              </Stack>
            </Box>
            <Divider sx={divider} />
          </>
        )}
        {privileges?.length > 0 && (
          <>
            <Box sx={section}>
              <Typography component="h2" sx={sectionTitle}>
                {t("Tourist Privileges")}
              </Typography>
              <Stack spacing={2}>
                {privileges.map((privilege) => (
                  <ListPrivilegeItemCard key={privilege.id} {...privilege} />
                ))}
              </Stack>
            </Box>
          </>
        )}
      </Card>
      <ImageModal
        open={openModal}
        imageSrc={activeImage}
        onClose={handleClose}
      />
    </>
  )
}
